/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */
// libraries
import React from 'react';
import { compose } from 'recompose';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { useQuery } from '@apollo/client';
import {
    object, string,
} from 'prop-types';
import mbpLogger from '@module-logger/mbp';
import loadable from '@loadable/component';

// components
import Media from 'react-media';
import GraphqlCategorySkeleton from '../GraphqlSkeletonComponents/CategoryPageSkeleton/CategoryPageSkeleton';
import GraphqlPassportCategoryPageContainer from './GraphqlPassportCategoryPageContainer';

// helpers, redux, queries
import {
    getSSRDeviceType,
} from '../../../../state/ducks/App/App-Selectors';
import { getFeatureFlag, getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { GRAPHQL_ENV } from '../../../gql';
import { findCategoryPageByUrlNoProducts } from '../../../gql/queries/findCategoryPageByURL';
import useExperimentServiceAttributes from '../../../helpers/experimentService/useExperimentServiceAttributes';
import { setWrapUpProductRefTitle } from '../../../../state/ducks/App/App-Actions';

const UnderNavTextWidgetContainer = loadable(() => import(/* webpackChunkName: "UnderNavTextWidgetContainer" */ '../GraphqlCommonComponents/GraphqlUnderNavTextWidget/GraphqlUnderNavTextWidgetContainer'));

// memoized to prevent re-renders, stopReRender ^
const GraphqlCategoryPageContainer = ({
    brand, page, presentation_family, ssrDeviceType,
}) => {
    const wrapUpTitleDispatch = useDispatch();
    const isExperimentsServiceEnabled = useSelector(getFeatureFlag('is-experiments-service-enabled-pwa'))?.['findCategoryPageByUrl'] || useSelector(getFeatureFlag('is-experiments-service-enabled-pwa'))?.['findCategoryById'];

    if (typeof window === 'undefined' && isExperimentsServiceEnabled) {
        return <GraphqlCategorySkeleton />;
    }

    const { targeting, context, isGraphqlTargetingEnabled  } = useExperimentServiceAttributes({ queryName: 'findCategoryPageByUrl' });
    const CATEGORY_PAGE_QUERY = findCategoryPageByUrlNoProducts(isGraphqlTargetingEnabled);
    const {
        loading, data, error,
    } = useQuery(CATEGORY_PAGE_QUERY, {
        variables: {
            brand: brand.domain,
            url: page.path,
            contentType: page.contentType,
            environment: GRAPHQL_ENV,
            locale: 'en-us',
            ...(isGraphqlTargetingEnabled ? { targeting } : {}),
        },
        context,
        onCompleted: (resData) => {
            const wrapUpTitle = resData?.findCategoryPageByUrl?.content?.entries?.[0]?.wrap_up_product_reference?.[0]?.title || '';
            if (wrapUpTitle) wrapUpTitleDispatch(setWrapUpProductRefTitle(wrapUpTitle));
        },
    });
    if (loading) {
        return <GraphqlCategorySkeleton />;
    }

    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            component: 'GraphqlCategoryPageContainer.js',
            message: `Error loading findCategoryPageByURL from url "${page.path}"`,
            env: GRAPHQL_ENV,
            error,
        });
        return null;
    }
    if (!data || !data?.findCategoryPageByUrl?.content || !data?.findCategoryPageByUrl?.category?.products?.length) {
        if (!data) {
            mbpLogger.logWarning({
                appName: process.env.npm_package_name,
                component: 'GraphqlCategoryPageContainer.js',
                message: `No data for findCategoryPageByURL from url "${page.path}"`,
                env: GRAPHQL_ENV,
            });
        }
        if (!data?.findCategoryPageByUrl?.category?.products?.length) {
            mbpLogger.logWarning({
                appName: process.env.npm_package_name,
                component: 'GraphqlCategoryPageContainer.js',
                message: `No products returned for findCategoryPageByURL from url "${page.path}"`,
                env: GRAPHQL_ENV,
            });
        }
        if (!data?.findCategoryPageByUrl?.content) {
            mbpLogger.logWarning({
                appName: process.env.npm_package_name,
                component: 'GraphqlCategoryPageContainer.js',
                message: `No category content returned for findCategoryPageByURL from url "${page.path}"`,
                env: GRAPHQL_ENV,
            });
        }
    }
    // setting category name and occasion from findCategoryPageByUrl for clickstream pageview events.
    const categoryData = data?.findCategoryPageByUrl;
    if (categoryData?.category) {
        const currentCategoryName = categoryData.category?.name || categoryData?.content?.entries?.[0]?.name || '';
        const currentCategoryOccasion = categoryData?.content?.entries?.[0]?.category_occasion?.[0] || '';
        const currentCategoryOccasions = categoryData?.content?.entries?.[0]?.category_occasion || '';
        // eslint-disable-next-line no-param-reassign
        page = {
            ...page,
            currentCategoryName,
            currentCategoryOccasion,
            currentCategoryOccasions,
        };
    }

    return (
        <>
            {data?.findCategoryPageByUrl?.showUnderNavText && (
                // renders only if screensize is >= 1025px
                // add min func to desktop-mobile
                <Media
                    query="(min-width: 1025px)"
                    defaultMatches={ssrDeviceType !== 'mobile'}
                    render={() => (
                        <UnderNavTextWidgetContainer page={page} isCanadianCategory={data.findCategoryPageByUrl.content?.entries?.[0]?.country === 'Canada'} />
                    )}
                />
            )}
            <GraphqlPassportCategoryPageContainer
                page={page}
                categoryData={data?.findCategoryPageByUrl || {}}
                brand={data?.findCategoryPageByUrl?.brand}
                presentation_family={presentation_family}
                deviceType={ssrDeviceType}
            />
        </>
    );
};

GraphqlCategoryPageContainer.propTypes = {
    brand: object.isRequired,
    page: object.isRequired,
    presentation_family: string.isRequired,
    ssrDeviceType: object.isRequired,
};

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    presentation_family: getPresentationFamily(state),
    ssrDeviceType: getSSRDeviceType(state),
});

const enhance = compose(
    withRouter,
    connect(mapStateToProps),
);

export default enhance(GraphqlCategoryPageContainer);
